import type { JSX } from 'react';
import { useState } from 'react';
import { DataTableFilters } from '@/shared/components/DataTableFilters';
import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { useCompanyUsers } from '../hooks/useCompanyUsers';
import { CreateCompanyUserDialog } from '../components/CreateCompanyUserDialog';
import { EditCompanyUserDialog } from '../components/EditCompanyUserDialog';
import { CompanyUsersContent } from '../components/CompanyUsersContent';
import { companyUserTableColumns } from '../config/companyUserTableColumns';
import type { CompanyUser } from '../types/company-admin.types';

function CenteredMessage({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="flex min-h-[400px] items-center justify-center">
      <p className="text-muted-foreground">{children}</p>
    </div>
  );
}

export function CompanyUsersPage(): JSX.Element {
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const user = useAuthStore((state) => state.user);
  const getEffectiveCompanyId = useAuthStore((state) => state.getEffectiveCompanyId);
  const isCompanyAdmin = useAuthStore((state) => state.isCompanyAdmin);
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);
  const impersonation = useAuthStore((state) => state.impersonation);
  const effectiveCompanyId = getEffectiveCompanyId();
  const { users, hasData, isLoading, isError } = useCompanyUsers(effectiveCompanyId);
  const {
    filteredSorted,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    sort,
    toggleSort,
    activeFilterCount,
  } = useTableFilters(users, companyUserTableColumns);
  const hasAccess = isCompanyAdmin() || (isSuperAdmin() && impersonation.isImpersonating);

  if (!hasAccess) return <CenteredMessage>You do not have access to this page.</CenteredMessage>;
  if (!effectiveCompanyId) return <CenteredMessage>No company context available.</CenteredMessage>;

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">Users</h1>
          {!isLoading && !(isError && !hasData) && <ResultCount count={filteredSorted.length} />}
        </div>
        <CreateCompanyUserDialog />
      </div>

      <DataTableFilters
        columns={companyUserTableColumns}
        filters={filters}
        activeFilterCount={activeFilterCount}
        onFilterChange={setFilter}
        onClearFilters={clearFilters}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      <CompanyUsersContent
        users={filteredSorted}
        isLoading={isLoading}
        isError={isError}
        hasData={hasData}
        hasActiveFilters={activeFilterCount > 0}
        sort={sort}
        onToggleSort={toggleSort}
        currentUserId={user?.id}
        onEdit={setEditingUser}
      />

      <EditCompanyUserDialog user={editingUser} onClose={() => setEditingUser(null)} />
    </div>
  );
}
